// src/components/Modal/Modal.jsx
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { FaTimes } from "react-icons/fa";

const SIZE_CLASS = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-3xl",
  "2xl": "max-w-4xl",
  "3xl": "max-w-5xl",
};

export function Modal({
  isOpen,
  onClose,
  children,
  size = "lg",
  labelledById,
  describedById,
  initialFocusSelector = "[data-autofocus]",
}) {
  const dialogRef = useRef(null);
  const hasFocusedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    // Autofocus une seule fois par ouverture
    if (!hasFocusedRef.current) {
      const t = setTimeout(() => {
        const target =
          dialogRef.current?.querySelector(initialFocusSelector) ||
          dialogRef.current;
        target?.focus?.();
        hasFocusedRef.current = true;
      }, 0);

      return () => {
        clearTimeout(t);
        root.style.overflow = prevOverflow;
        hasFocusedRef.current = false;
      };
    }

    return () => {
      root.style.overflow = prevOverflow;
      hasFocusedRef.current = false;
    };
  }, [isOpen, initialFocusSelector]);

  // Fermer uniquement si le down/up commencent ET finissent sur l’overlay
  const mouseDownOnOverlay = useRef(false);
  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) mouseDownOnOverlay.current = true;
  };
  const handleOverlayMouseUp = (e) => {
    if (mouseDownOnOverlay.current && e.target === e.currentTarget) onClose?.();
    mouseDownOnOverlay.current = false;
  };

  // Pare-feu clavier en phase BUBBLING (après l’input)
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      e.preventDefault();
      onClose?.();
      return;
    }
    // Empêche les hotkeys globaux de capter les frappes
    e.stopPropagation();
  };
  const handleKeyUp = (e) => {
    e.stopPropagation();
  };
  const handleKeyPress = (e) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
      aria-modal
      role="dialog"
      aria-labelledby={labelledById}
      aria-describedby={describedById}
    >
      <div
        ref={dialogRef}
        className={`relative w-full ${
          SIZE_CLASS[size] || SIZE_CLASS.lg
        } flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92svh] sm:max-h-[92dvh] outline-none`}
        tabIndex={-1}
        // IMPORTANT : bubbling only (pas de *Capture*)
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onKeyPress={handleKeyPress}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

export function ModalHeader({ title, subtitle, onClose, id }) {
  return (
    <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
      <div>
        <h3
          id={id}
          className="text-white text-lg sm:text-xl font-bold tracking-tight"
        >
          {title}
        </h3>
        {subtitle ? (
          <p className="text-white/90 text-xs sm:text-sm">{subtitle}</p>
        ) : null}
      </div>
      <button
        onClick={onClose}
        className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
        aria-label="Fermer la fenêtre modale"
      >
        <FaTimes />
      </button>
    </div>
  );
}

export function ModalBody({ children, id }) {
  return (
    <div
      id={id}
      className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 overscroll-contain"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      {children}
    </div>
  );
}

export function ModalFooter({ children }) {
  return (
    <div className="sticky bottom-0 z-10">
      <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
      <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
        {children}
      </div>
    </div>
  );
}
